import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Button, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import Swal from "sweetalert2";

import { appApi, useGetAllProjectsForMatrixQuery, useGetMyProjectsQuery, useGetCompaniesQuery } from "../../../store/appApi";
import { setActiveProject, selectActiveProjectId } from "../../../store/projectSlice";
import { selectUserRole, selectUser } from "../../../store/authSlice";
import { createProjectApi } from "../../../services/project";

// ── Shared project card ───────────────────────────────────────────────────────

const ProjectCard = ({ project, isActive, onSelect }) => {
  const id = project._id?.toString() ?? project.id?.toString();
  return (
    <div
      className="card mb-0 h-100"
      style={{
        cursor: "pointer",
        border: isActive ? "2px solid #324336" : "1px solid rgba(0,0,0,.1)",
        borderRadius: 12,
        transition: "box-shadow 0.2s, transform 0.2s",
      }}
      onClick={onSelect}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 4px 20px rgba(102,88,221,0.22)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "";
        e.currentTarget.style.transform = "";
      }}
    >
      <div className="card-body d-flex flex-column gap-2 p-4">
        <div className="d-flex align-items-start justify-content-between">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: 44,
              height: 44,
              background: "#3243361a",
              color: "#324336",
              fontSize: 20,
            }}
          >
            <i className="fas fa-building" />
          </div>
          {isActive && <span className="badge bg-primary">Active</span>}
        </div>
        <h6 className="card-title mb-0 mt-2" style={{ fontWeight: 600, lineHeight: 1.3 }}>
          {project.projectName}
        </h6>
        {project.city && (
          <p className="text-muted mb-0" style={{ fontSize: 13 }}>
            <i className="ti-location-pin me-1" />
            {project.city}
          </p>
        )}
        {project.reraNumber && (
          <p className="text-muted mb-0" style={{ fontSize: 12 }}>
            RERA: {project.reraNumber}
          </p>
        )}
      </div>
    </div>
  );
};

// ── Create-project modal (admin only) ────────────────────────────────────────

const CreateProjectModal = ({ show, onHide, onCreated }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    projectName: "",
    city: "",
    underCompany: "",
    totalProjectArea: "",
    reraNumber: "",
  });
  const [errors, setErrors] = useState({});

  const { data: companiesResponse } = useGetCompaniesQuery(
    { page: 1, limit: 100 },
    { skip: !show }
  );
  const companies = Array.isArray(companiesResponse?.data) ? companiesResponse.data : [];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.projectName.trim()) e.projectName = "Project name is required.";
    if (!form.city.trim()) e.city = "City is required.";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);
      const response = await createProjectApi({
        projectName: form.projectName.trim(),
        city: form.city.trim(),
        totalProjectArea: form.totalProjectArea.trim(),
        reraNumber: form.reraNumber.trim(),
        underCompany: form.underCompany || undefined,
      });

      const newProject = response.data?.project;

      if (newProject) {
        dispatch(
          setActiveProject({
            id: newProject._id?.toString() ?? newProject.id,
            name: newProject.projectName,
            city: newProject.city,
          })
        );
      }

      dispatch(appApi.util.invalidateTags(["Projects"]));
      onHide();

      await Swal.fire({
        icon: "success",
        title: "Project Created!",
        text: `${form.projectName} is ready. Opening your workspace…`,
        timer: 1800,
        showConfirmButton: false,
      });

      if (newProject) {
        const id = newProject._id?.toString() ?? newProject.id;
        onCreated(id);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to create project. Please try again.";
      Swal.fire({ icon: "error", title: "Error", text: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onHide();
      setForm({ projectName: "", city: "", underCompany: "", totalProjectArea: "", reraNumber: "" });
      setErrors({});
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static">
      <Modal.Header closeButton={!loading}>
        <Modal.Title style={{ fontWeight: 700, fontSize: 17 }}>
          Create New Project
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>
              Project Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              name="projectName"
              type="text"
              placeholder="e.g. Sunrise Towers"
              value={form.projectName}
              onChange={handleChange}
              isInvalid={!!errors.projectName}
              disabled={loading}
            />
            <Form.Control.Feedback type="invalid">{errors.projectName}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              City <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              name="city"
              type="text"
              placeholder="e.g. Mumbai"
              value={form.city}
              onChange={handleChange}
              isInvalid={!!errors.city}
              disabled={loading}
            />
            <Form.Control.Feedback type="invalid">{errors.city}</Form.Control.Feedback>
          </Form.Group>

          {companies.length > 0 && (
            <Form.Group className="mb-3">
              <Form.Label>Company (optional)</Form.Label>
              <Form.Select
                name="underCompany"
                value={form.underCompany}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="">Select a company…</option>
                {companies.map((c) => (
                  <option key={c._id || c.id} value={c._id || c.id}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>RERA Number (optional)</Form.Label>
            <Form.Control
              name="reraNumber"
              type="text"
              placeholder="Enter RERA number"
              value={form.reraNumber}
              onChange={handleChange}
              disabled={loading}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Total Project Area (optional)</Form.Label>
            <Form.Control
              name="totalProjectArea"
              type="text"
              placeholder="e.g. 50,000 sq ft"
              value={form.totalProjectArea}
              onChange={handleChange}
              disabled={loading}
            />
          </Form.Group>

          <div className="d-flex gap-2 justify-content-end mt-4">
            <Button variant="light" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              style={{ background: "#324336", border: "none", minWidth: 130 }}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Creating…
                </>
              ) : (
                "Create Project"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

// ── Main screen ───────────────────────────────────────────────────────────────

const ZeroStateScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);

  const userRole = useSelector(selectUserRole);
  const user = useSelector(selectUser);
  const activeProjectId = useSelector(selectActiveProjectId);
  const isSuperAdmin = userRole === "super-admin";

  // JWT-persisted project IDs for staff — used as fallback source of truth
  const userProjectIds = useSelector((state) => state.auth.user?.projectIds ?? []);

  // All users fetch the full project list so staff can filter by their assigned IDs
  const { data: allProjectsData, isLoading: allLoading } =
    useGetAllProjectsForMatrixQuery();

  // Staff also call getMyProjects for fresh DB assignments (supplements JWT data)
  const { data: myProjectsData, isLoading: myLoading } =
    useGetMyProjectsQuery(undefined, { skip: isSuperAdmin });

  const isLoading = isSuperAdmin ? allLoading : (allLoading || myLoading);

  const allProjects = allProjectsData?.data ?? allProjectsData?.projects ?? [];

  const projects = isSuperAdmin
    ? allProjects
    : (() => {
        // Primary: projects returned directly from the DB via getMyProjects
        const fromApi = myProjectsData?.projects ?? [];
        // Fallback: filter the full project list by JWT-stored project IDs
        const fromJwt = allProjects.filter((p) =>
          userProjectIds.includes(p._id?.toString() ?? p.id?.toString())
        );
        // Merge: unique projects from both sources (API takes precedence)
        if (fromApi.length > 0) return fromApi;
        return fromJwt;
      })();

  const handleSelect = (project) => {
    const id = project._id?.toString() ?? project.id?.toString();
    dispatch(setActiveProject({ id, name: project.projectName, city: project.city }));
    navigate(`/dashboard/project/${id}`);
  };

  const handleCreated = (newId) => {
    window.location.replace(`/dashboard/project/${newId}`);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f4f6f9",
        padding: "40px 24px",
        overflowY: "auto",
      }}
    >
      <div style={{ maxWidth: 960, margin: "0 auto" }}>

        {/* ── Branding / Welcome ──────────────────────────────────────── */}
        <div className="text-center mb-5">
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: 14,
              background: "#324336",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <i className="fas fa-building" style={{ fontSize: 26, color: "#fff" }} />
          </div>
          <h3 style={{ fontWeight: 700, color: "#2c2c2c", marginBottom: 4 }}>
            Welcome{user?.name ? `, ${user.name}` : ""}!
          </h3>
          <p style={{ color: "#888", fontSize: 14 }}>
            {isSuperAdmin
              ? "Select a project to open its workspace, or create a new one."
              : "Select a project to open its workspace."}
          </p>
        </div>

        {/* ── Loading ─────────────────────────────────────────────────── */}
        {isLoading ? (
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
          </div>

        ) : projects.length > 0 ? (
          /* ── Projects grid ────────────────────────────────────────── */
          <>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6
                className="text-uppercase text-muted fw-bold mb-0"
                style={{ letterSpacing: 1, fontSize: 11 }}
              >
                {isSuperAdmin ? "All Projects" : "Your Projects"}
              </h6>
              {isSuperAdmin && (
                <Button
                  size="sm"
                  onClick={() => setShowModal(true)}
                  style={{
                    background: "#324336",
                    border: "none",
                    borderRadius: 6,
                    fontWeight: 600,
                    fontSize: 13,
                    padding: "6px 16px",
                  }}
                >
                  <i className="ti-plus me-1" />
                  Create Project
                </Button>
              )}
            </div>

            <div className="row g-3">
              {projects.map((project) => {
                const id = project._id?.toString() ?? project.id?.toString();
                return (
                  <div key={id} className="col-sm-6 col-md-4 col-lg-3">
                    <ProjectCard
                      project={project}
                      isActive={activeProjectId === id}
                      onSelect={() => handleSelect(project)}
                    />
                  </div>
                );
              })}
            </div>
          </>

        ) : isSuperAdmin ? (
          /* ── Admin zero-state: no projects yet ────────────────────── */
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              boxShadow: "0 4px 32px rgba(0,0,0,0.07)",
              padding: "56px 40px",
              maxWidth: 480,
              margin: "0 auto",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                background: "#3243361a",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 24px",
              }}
            >
              <i className="ti-plus" style={{ fontSize: 32, color: "#324336" }} />
            </div>
            <h4 style={{ fontWeight: 700, color: "#2c2c2c", marginBottom: 12 }}>
              No Projects Yet
            </h4>
            <p style={{ color: "#888", fontSize: 14, marginBottom: 32, lineHeight: 1.7 }}>
              Create your first project to unlock Staff management, Finance,
              Inventory, and all other modules.
            </p>
            <Button
              size="lg"
              onClick={() => setShowModal(true)}
              style={{
                background: "#324336",
                border: "none",
                borderRadius: 8,
                fontWeight: 600,
                padding: "12px 36px",
                fontSize: 15,
              }}
            >
              <i className="ti-plus me-2" />
              Create a Project
            </Button>
          </div>

        ) : (
          /* ── Staff zero-state: no projects assigned ───────────────── */
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              boxShadow: "0 4px 32px rgba(0,0,0,0.07)",
              padding: "56px 40px",
              maxWidth: 480,
              margin: "0 auto",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                background: "#ffc1071a",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 24px",
              }}
            >
              <i className="ti-lock" style={{ fontSize: 32, color: "#ffc107" }} />
            </div>
            <h4 style={{ fontWeight: 700, color: "#2c2c2c", marginBottom: 12 }}>
              No Projects Assigned
            </h4>
            <p style={{ color: "#888", fontSize: 14, lineHeight: 1.7, marginBottom: 0 }}>
              You don't have access to any projects yet. Contact your
              administrator to get assigned to a project.
            </p>
          </div>
        )}
      </div>

      {/* Create project modal (admin only) */}
      <CreateProjectModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onCreated={handleCreated}
      />
    </div>
  );
};

export default ZeroStateScreen;
